/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { useEffect, useRef } from 'react';

/** Return value from the useTimeoutCleanup hook */
export interface UseTimeoutCleanupResult {
  /** Register a timeout for automatic cleanup on unmount */
  registerTimeout: (timeoutId: NodeJS.Timeout) => void;
}

/**
 * Registers timeouts for automatic cleanup on component unmount
 *
 * @example
 * ```tsx
 * function ComponentWithTimeouts() {
 *   const { registerTimeout } = useTimeoutCleanup();
 *   const [message, setMessage] = useState('');
 *
 *   const handleClick = () => {
 *     setMessage('Saving...');
 *
 *     registerTimeout(
 *       setTimeout(() => {
 *         setMessage('Saved!');
 *       }, 1000)
 *     );
 *   };
 *
 *   return (
 *     <div>
 *       <Button onPress={handleClick}>Save</Button>
 *       <span>{message}</span>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns {@link UseTimeoutCleanupResult} Timeout registration function.
 */
export function useTimeoutCleanup(): UseTimeoutCleanupResult {
  const timeoutIdsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const registerTimeout = (timeoutId: NodeJS.Timeout) => {
    timeoutIdsRef.current.add(timeoutId);
  };

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach(clearTimeout);
      timeoutIdsRef.current.clear();
    };
  }, []);

  return { registerTimeout };
}
